import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ShowPackage,
  type TShowPackageReference,
  type TShowPackageImportChange,
  type TShowPackageImportClubAffiliation,
} from '@schemas'
import { Button } from '@/components/ui/button'
import { downloadJson, useOwnership } from '@/api/show-transfer'
import { CandidateSelect } from './CandidateSelect'
import { Check, JsonFile, privacyWarning } from './files'
import { referenceKey, type ReviewController } from './usePackageReview'
import { StatusNotice, WorkflowFooter } from './WorkflowLayout'

const names: Record<string, string> = {
  show: 'Show',
  organizations: 'Organizations',
  people: 'People',
  exhibitors: 'Exhibitor records',
  entrants: 'Entrants',
  exhibits: 'Exhibit records',
  identifiers: 'Exhibit ID records',
  clubs: 'Clubs',
  models: 'Catalog models',
  subtypes: 'Catalog subtypes',
  divisions: 'Divisions',
  classes: 'Classes',
  awards: 'Awards',
  staff: 'Staff assignments',
  entries: 'Exhibits',
  series: 'Show series',
  seriesIdentities: 'Series identities',
  priorEditions: 'Prior editions',
}
const clubAffiliationCodes = ['CLUB_AFFILIATION_REVIEW_REQUIRED', 'CLUB_AFFILIATION_CHOICE_INVALID']
export function PackageSummary({ value }: { value: unknown }) {
  const parsed = useMemo(() => ShowPackage.safeParse(value), [value])
  if (!parsed.success) return null
  const pkg = parsed.data
  const count = (type: keyof typeof pkg.records) => pkg.records[type]?.length ?? 0
  return (
    <div className="space-y-2 rounded-lg bg-muted/50 p-5 text-sm">
      <h3 className="font-semibold">{pkg.show.name}</h3>
      <p>
        {pkg.show.startDate.slice(0, 10)} – {pkg.show.endDate.slice(0, 10)} · Show status:{' '}
        {pkg.show.status}
      </p>
      <p>
        {count('entrants')} entrants · {count('entries')} exhibits · {count('classes')} classes ·{' '}
        {count('awards')} awards
      </p>
      <details>
        <summary className="text-muted-foreground">Personal information in this file</summary>
        <p className="mt-2">
          {pkg.privacyProfile === 'full-contact' ? 'Full contact details' : 'Limited personal data'}{' '}
          · {privacyWarning}
        </p>
      </details>
    </div>
  )
}
export function PackageReviewWorkspace({
  controller: c,
  wizard = false,
  onBack,
  onReceiptSaved,
}: {
  controller: ReviewController
  wizard?: boolean
  onBack?: () => void
  onReceiptSaved?: () => void
}) {
  const parsed = useMemo(() => ShowPackage.safeParse(c.packageValue), [c.packageValue]),
    pkg = parsed.success ? parsed.data : undefined
  const data = c.review?.preview
  const clubAffiliations = data?.clubAffiliations ?? []
  const [selected, setSelected] = useState('')
  const [exception, setException] = useState('')
  const [savedChoice, setSavedChoice] = useState('')
  const [savedResolution, setSavedResolution] = useState('')
  const [editSaved, setEditSaved] = useState(false)
  const [recoveryAcknowledged, setRecoveryAcknowledged] = useState(false)
  const [allChanges, setAllChanges] = useState(false)
  const panel = useRef<HTMLDivElement>(null)
  const focusedReview = useRef<string | undefined>(undefined)
  const ownership = useOwnership(
    c.result?.destinationShowId ?? (data?.creationContext.show.exists ? data.destinationShowId : '')
  )
  const revision = ownership.data ? JSON.stringify(ownership.data) : undefined
  const previousRevision = useRef(revision)
  useEffect(() => {
    if (
      revision &&
      previousRevision.current &&
      previousRevision.current !== revision &&
      !c.locked &&
      !c.result
    )
      c.refresh()
    previousRevision.current = revision
  }, [revision, c])
  const packages = useMemo(
    () => [
      ...(pkg ? [pkg] : []),
      ...[...c.baselines]
        .reverse()
        .map(value => ShowPackage.safeParse(value))
        .flatMap(p => (p.success ? [p.data] : [])),
    ],
    [pkg, c.baselines]
  )
  const recordsByReference = useMemo(() => {
    const records = new Map<string, Record<string, unknown>>()
    for (const p of packages) {
      const add = (type: TShowPackageReference['type'], row: { key: string }) => {
        const key = referenceKey({ packageKey: p.packageKey, type, key: row.key })
        if (!records.has(key)) records.set(key, row as unknown as Record<string, unknown>)
      }
      add('show', p.show)
      for (const [type, rows] of Object.entries(p.records))
        for (const row of rows ?? []) add(type as TShowPackageReference['type'], row)
    }
    return records
  }, [packages])
  const record = useCallback(
    (reference: TShowPackageReference) => recordsByReference.get(referenceKey(reference)),
    [recordsByReference]
  )
  const label = useCallback(
    (reference: TShowPackageReference) => {
      const row = record(reference)
      if (reference.type === 'identifiers' && row?.value) return `Exhibit ID ${row.value}`
      return String(
        row?.name ??
          row?.title ??
          row?.code ??
          (row?.exhibitId
            ? `Exhibit ID ${row.exhibitId}`
            : (row?.role ?? names[reference.type] ?? reference.type))
      )
    },
    [record]
  )
  const relationshipType = (field: string, reference: TShowPackageReference) => {
    const types: Record<string, TShowPackageReference['type']> = {
      organizationKey: 'organizations',
      orgId: 'organizations',
      organizationId: 'organizations',
      personKey: 'people',
      personId: 'people',
      judgeKey: 'people',
      judgeId: 'people',
      exhibitorKey: 'exhibitors',
      exhibitorId: 'exhibitors',
      assignedExhibitorKey: 'exhibitors',
      assignedExhibitorId: 'exhibitors',
      clubKey: 'clubs',
      clubId: 'clubs',
      divisionKey: 'divisions',
      divisionId: 'divisions',
      classKey: 'classes',
      classId: 'classes',
      currentClassId: 'classes',
      classKeys: 'classes',
      classIds: 'classes',
      previousClassKey: 'classes',
      previousClassId: 'classes',
      modelKey: 'models',
      catalogClassId: 'models',
      subtypeKey: 'subtypes',
      catalogSubtypeId: 'subtypes',
      entryKey: 'entries',
      entryId: 'entries',
      assignedEntryKey: 'entries',
      assignedEntryId: 'entries',
      deletedEntryKey: 'entries',
      exhibitKey: 'exhibits',
      entrantKey: 'entrants',
      entrantId: 'entrants',
      seriesKey: 'series',
      seriesId: 'series',
      identityKey: 'seriesIdentities',
      identityId: 'seriesIdentities',
      judgeAssignmentKeys: 'staff',
      judgeAssignments: 'staff',
    }
    if (field === 'exhibitId' && ['scores', 'awards'].includes(reference.type)) return 'exhibits'
    return types[field]
  }
  const format = (value: unknown, reference: TShowPackageReference, field = ''): string => {
    if (value === null) return 'Clear value'
    if (value === undefined) return 'Not supplied; keep current value'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'object') {
      if (Array.isArray(value))
        return value.map(item => format(item, reference, field)).join(', ') || 'None'
      return Object.entries(value)
        .map(([key, item]) => `${key.replace(/([A-Z])/g, ' $1')}: ${format(item, reference, key)}`)
        .join('; ')
    }
    const type = relationshipType(field, reference)
    if (!type) return String(value)
    if (/Keys?$/.test(field)) {
      const target = { packageKey: reference.packageKey, type, key: String(value) }
      if (record(target)) return label(target)
    } else {
      const destination = data?.destinationRecords.find(
        r => r.type === type && r.destinationId === value
      )
      if (destination)
        return `${destination.label}${destination.secondary ? ` — ${destination.secondary}` : ''}`
      const club =
        type === 'clubs' &&
        clubAffiliations.find(
          a => a.destinationId === value || a.candidates.some(o => o.destinationId === value)
        )
      if (club)
        return club.destinationId === value && club.action === 'create'
          ? `${club.name} — new club`
          : (club.candidates.find(o => o.destinationId === value)?.label ?? club.name)
    }
    return String(value)
  }
  const savedMappings = c.choices.mappings.map(mapping => ({
    reference: mapping.reference,
    description: mapping.action,
  }))
  const mappingKeys = useMemo(
    () => new Set(c.choices.mappings.map(mapping => referenceKey(mapping.reference))),
    [c.choices.mappings]
  )
  const proposals = useMemo(
    () =>
      data?.creationContext.proposedCreates.filter(
        ref => !(c.bundle && ref.type === 'show') && !mappingKeys.has(referenceKey(ref))
      ) ?? [],
    [data?.creationContext.proposedCreates, c.bundle, mappingKeys]
  )
  const pendingChanges = useMemo(
    () =>
      (data?.changes ?? []).filter(
        change =>
          change.action !== 'conflict' ||
          !c.choices.resolutions.some(
            choice =>
              referenceKey(choice.reference) === referenceKey(change.reference) &&
              choice.field === change.field
          )
      ),
    [data?.changes, c.choices.resolutions]
  )
  const pendingBlockers = useMemo(
    () =>
      (data?.blockers ?? []).filter(blocker => {
        if (!blocker.reference) return true
        const key = referenceKey(blocker.reference)
        if (blocker.code === 'MAPPING_REQUIRED' && mappingKeys.has(key)) return false
        if (
          ['FIELD_CONFLICT', 'IMMUTABLE_IDENTITY', 'MEMBERSHIP_CONFLICT'].includes(blocker.code) &&
          c.choices.resolutions.some(choice => referenceKey(choice.reference) === key)
        )
          return pendingChanges.some(
            change => change.action === 'conflict' && referenceKey(change.reference) === key
          )
        if (['REMOVAL_REVIEW_REQUIRED', 'CONSUMED_IDENTITY_REMOVAL'].includes(blocker.code))
          return !c.choices.resolutions.some(
            choice => referenceKey(choice.reference) === key && choice.field === '$remove'
          )
        return true
      }),
    [data?.blockers, mappingKeys, pendingChanges, c.choices.mappings, c.choices.resolutions]
  )
  type Decision = {
    id: string
    title: string
    type?: string
    reference?: TShowPackageReference
    message?: string
  }
  const decisions = useMemo(() => {
    const proposalKeys = new Set(proposals.map(referenceKey))
    const groupCounts = new Map<string, number>()
    for (const ref of proposals) groupCounts.set(ref.type, (groupCounts.get(ref.type) ?? 0) + 1)
    const decisions: Decision[] = [...groupCounts].map(([type, count]) => ({
      id: `group:${type}`,
      title: `${names[type] ?? type} (${count})`,
      type,
    }))
    const decisionKeys = new Set(decisions.map(d => d.id))
    for (const blocker of [...pendingBlockers].sort(
      (a, b) => Number(a.code === 'MAPPING_REQUIRED') - Number(b.code === 'MAPPING_REQUIRED')
    )) {
      if (
        blocker.code === 'MAPPING_REQUIRED' &&
        blocker.reference &&
        proposalKeys.has(referenceKey(blocker.reference))
      )
        continue
      const id = blocker.reference ? referenceKey(blocker.reference) : blocker.code
      if (!decisionKeys.has(id)) {
        decisionKeys.add(id)
        decisions.push({
          id,
          title: blocker.reference
            ? label(blocker.reference)
            : clubAffiliationCodes.includes(blocker.code)
              ? 'Club affiliations'
              : 'Import requires attention',
          reference: blocker.reference,
          message: blocker.message,
        })
      }
    }
    for (const change of pendingChanges)
      if (change.action === 'conflict') {
        const id = referenceKey(change.reference)
        if (!decisionKeys.has(id)) {
          decisionKeys.add(id)
          decisions.push({ id, title: label(change.reference), reference: change.reference })
        }
      }
    return decisions
  }, [pendingBlockers, pendingChanges, proposals, label])
  const active = decisions.find(d => d.id === selected) ?? decisions[0]
  useEffect(() => {
    if (c.updating || !c.valid || !data || focusedReview.current === data.reviewToken) return
    focusedReview.current = data.reviewToken
    setSelected(active?.id ?? '')
    if (
      panel.current?.contains(document.activeElement) ||
      document.activeElement?.tagName === 'BUTTON'
    )
      panel.current?.focus()
  }, [data, c.updating, c.valid, active?.id])
  const disabled = c.disabled || c.reading || c.updating || c.applying || c.uncertain
  const save = (choices: typeof c.choices) => c.changeChoices(choices)
  const mapRecord = (
    reference: TShowPackageReference,
    action: 'create' | 'map',
    destinationId?: string
  ) =>
    c.changeChoices({
      ...c.choices,
      mappings: [
        ...c.choices.mappings.filter(m => referenceKey(m.reference) !== referenceKey(reference)),
        ...(action === 'create'
          ? [{ reference, action } as const]
          : destinationId
            ? [{ reference, action, destinationId } as const]
            : []),
      ],
    })
  function mappingEditor(reference: TShowPackageReference) {
    const mapping = c.choices.mappings.find(
      m => referenceKey(m.reference) === referenceKey(reference)
    )
    const blocked = data?.blockers.some(
      b =>
        b.reference &&
        referenceKey(b.reference) === referenceKey(reference) &&
        /IMMUTABLE|CONSUMED|CANONICAL_ORIGIN/.test(b.code)
    )
    return (
      <div className="space-y-3">
        <h4 className="font-semibold">{label(reference)}</h4>
        {c.bundle && reference.type === 'show' ? (
          <p>Destination show is fixed by the transfer preparation.</p>
        ) : blocked ? (
          <p>This identity cannot be overridden. Resolve the server blocker before continuing.</p>
        ) : (
          <>
            <CandidateSelect
              key={`${referenceKey(reference)}:${data?.destinationShowId ?? ''}`}
              type={reference.type}
              showId={data?.destinationShowId}
              value={mapping?.action === 'map' ? mapping.destinationId : undefined}
              onChange={id => mapRecord(reference, 'map', id)}
            />
            {!pkg?.removals.some(
              ref => ref.type === reference.type && ref.key === reference.key
            ) && (
              <Button variant="outline" onClick={() => mapRecord(reference, 'create')}>
                Create this as a new record
              </Button>
            )}
          </>
        )}
        <Button variant="outline" onClick={() => mapRecord(reference, 'map')}>
          Use established identity
        </Button>
        <Button onClick={() => void c.preview()}>Save choice</Button>
        <details>
          <summary>Technical identity</summary>
          <p className="break-all">
            {reference.type} / {reference.key}
          </p>
        </details>
      </div>
    )
  }
  /** Typed club names: link to a listed same-name club, add a club, or keep the typed name. */
  function clubAffiliationEditor(items: TShowPackageImportClubAffiliation[]) {
    // A club record in the file is chosen by its stable key; its planned ID changes per preview.
    const option = (candidate: TShowPackageImportClubAffiliation['candidates'][number]) =>
      candidate.clubKey ? `file:${candidate.clubKey}` : `link:${candidate.destinationId}`
    const choose = (nameKey: string, value: string) =>
      save({
        ...c.choices,
        clubAffiliations: [
          ...c.choices.clubAffiliations.filter(choice => choice.nameKey !== nameKey),
          // Explicit choices replace a decision remembered from an earlier import.
          ...(value === 'keep'
            ? [{ nameKey, destinationId: null }]
            : value === 'create'
              ? [{ nameKey, create: true as const }]
              : value.startsWith('file:')
                ? [{ nameKey, clubKey: value.slice(5) }]
                : value.startsWith('link:')
                  ? [{ nameKey, destinationId: value.slice(5) }]
                  : []),
        ],
      })
    return (
      <ul className="space-y-3">
        {items.map(item => {
          const choice = c.choices.clubAffiliations.find(chosen => chosen.nameKey === item.nameKey)
          const linked = item.candidates.find(o => o.destinationId === item.destinationId)
          const value = choice
            ? 'create' in choice
              ? 'create'
              : 'clubKey' in choice
                ? `file:${choice.clubKey}`
                : choice.destinationId === null
                  ? 'keep'
                  : `link:${choice.destinationId}`
            : item.action === 'link' && linked
              ? option(linked)
              : item.action === 'keepName'
                ? 'keep'
                : item.action === 'create'
                  ? 'create'
                  : ''
          return (
            <li key={item.nameKey}>
              <label className="block space-y-1">
                <span>
                  {item.name} ({item.affiliations}{' '}
                  {item.affiliations === 1 ? 'entrant or exhibit' : 'entrants or exhibits'})
                </span>
                <select
                  className="block h-10 w-full max-w-full rounded-md border bg-background px-3 text-sm"
                  value={value}
                  disabled={disabled}
                  onChange={event => choose(item.nameKey, event.target.value)}
                >
                  {!value && <option value="">Choose a club</option>}
                  {item.candidates.map(candidate => (
                    <option key={candidate.destinationId} value={option(candidate)}>
                      Link to {candidate.label}
                      {candidate.secondary ? ` — ${candidate.secondary}` : ''}
                    </option>
                  ))}
                  {!item.candidates.length && <option value="create">Add as a new club</option>}
                  <option value="keep">Keep the typed name without a club</option>
                </select>
              </label>
            </li>
          )
        })}
      </ul>
    )
  }
  function resolutions(reference: TShowPackageReference, field: string, allowed: string[]) {
    return (
      <div className="flex flex-wrap gap-2">
        {(['keepDestination', 'usePackage'] as const).map(action => (
          <Button
            key={action}
            variant={field === '$remove' && action === 'usePackage' ? 'destructive' : 'outline'}
            disabled={disabled || !allowed.includes(action)}
            onClick={() =>
              save({
                ...c.choices,
                resolutions: [
                  ...c.choices.resolutions.filter(
                    r =>
                      !(referenceKey(r.reference) === referenceKey(reference) && r.field === field)
                  ),
                  { reference, field, action },
                ],
              })
            }
          >
            {action === 'keepDestination'
              ? 'Keep destination'
              : field === '$remove'
                ? 'Approve removal'
                : 'Use package value'}
          </Button>
        ))}
      </div>
    )
  }
  function comparison(change: TShowPackageImportChange, i: number) {
    return (
      <div key={i} className="border rounded p-3 space-y-2">
        <p>
          {label(change.reference)} ·{' '}
          {change.field === '$remove' ? 'Removal' : (change.field ?? change.action)}
        </p>
        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="font-medium">From this file</dt>
            <dd className="break-words">
              {change.action === 'remove'
                ? 'Remove this record'
                : format(change.incoming, change.reference, change.field)}
            </dd>
          </div>
          <div>
            <dt className="font-medium">On this server</dt>
            <dd className="break-words">
              {Object.hasOwn(change, 'before')
                ? format(change.before, change.reference, change.field)
                : 'No current value'}
            </dd>
          </div>
        </dl>
        {change.field &&
          change.action === 'conflict' &&
          resolutions(
            change.reference,
            change.field,
            change.allowedResolutions ?? ['keepDestination', 'usePackage']
          )}
      </div>
    )
  }
  const summary = (
    <div className="space-y-3">
      <h3 className="text-xl font-semibold">
        {c.canApply && !decisions.length
          ? data?.creationContext.show.exists
            ? 'Ready to update this show'
            : 'Ready to create this show'
          : 'Included records'}
      </h3>
      {c.canApply && !decisions.length && (
        <StatusNotice tone="success" role="status" title="Ready to import">
          No decisions needed
        </StatusNotice>
      )}
      <p>
        Imported copies remain read-only unless editing is already enabled or you explicitly
        designate or activate this copy. Results are not published automatically.
      </p>
      <table className="w-full text-left text-sm [&_th]:py-3 [&_th]:font-medium [&_td]:py-3 [&_tr]:border-b [&_td:last-child]:text-right [&_th:last-child]:text-right">
        <caption className="sr-only">Included records</caption>
        <thead>
          <tr>
            <th>Group</th>
            <th>Included</th>
            <th>Changes</th>
          </tr>
        </thead>
        <tbody>
          {[
            'show',
            'organizations',
            'divisions',
            'classes',
            'entrants',
            'entries',
            'awards',
            'staff',
          ]
            .filter(
              type =>
                type === 'show' ||
                (pkg?.records[type as keyof typeof pkg.records]?.length ?? 0) > 0 ||
                data?.changes.some(change => change.reference.type === type)
            )
            .map(type => (
              <tr key={type}>
                <th>{names[type]}</th>
                <td>
                  {type === 'show'
                    ? 1
                    : (pkg?.records[type as keyof typeof pkg.records]?.length ?? 0)}
                </td>
                <td className="text-muted-foreground">
                  {(['create', 'update', 'remove', 'map', 'preserve', 'conflict'] as const)
                    .flatMap(action => {
                      const count = new Set(
                        data?.changes
                          .filter(
                            change => change.reference.type === type && change.action === action
                          )
                          .map(change => referenceKey(change.reference))
                      ).size
                      return count
                        ? [
                            `${count} ${{ create: 'new', update: 'updated', remove: 'removed', map: 'linked', preserve: 'kept', conflict: 'to review' }[action]}`,
                          ]
                        : []
                    })
                    .join(' · ') || '—'}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {!!data && data.ignoredLegacy.priorEditions + data.ignoredLegacy.editionMappings > 0 && (
        <p className="text-sm text-muted-foreground">
          This file carries a previous-edition link or class matches saved before show series
          decided them. They are not imported; the show’s series and its class matches across years
          decide comparisons.
        </p>
      )}
      {!!clubAffiliations.length && (
        <section aria-label="Clubs from typed names" className="space-y-2">
          <h4 className="font-semibold">Clubs from typed names</h4>
          <p className="text-sm text-muted-foreground">
            Entrants and exhibits that list a club only by name are linked to the club with that
            name. A club is added to the show organization when this server has none.
          </p>
          <fieldset disabled={disabled}>{clubAffiliationEditor(clubAffiliations)}</fieldset>
        </section>
      )}
      <details>
        <summary>Supporting records</summary>
        {pkg &&
          Object.entries(pkg.records)
            .filter(
              ([type]) =>
                ![
                  'organizations',
                  'divisions',
                  'classes',
                  'entrants',
                  'entries',
                  'awards',
                  'staff',
                ].includes(type)
            )
            .map(([type, rows]) => (
              <p key={type}>
                {names[type] ?? type}: {rows?.length ?? 0}
                {['exhibits', 'identifiers'].includes(type) ? ' supporting exhibit records' : ''}
              </p>
            ))}
      </details>
      <details onToggle={e => setAllChanges(e.currentTarget.open)}>
        <summary>All changes ({data?.changes.length ?? 0})</summary>
        {allChanges && data?.changes.map(comparison)}
      </details>
    </div>
  )
  return (
    <section aria-label="Review show import" className="space-y-4">
      {!wizard && (
        <h2 className="text-xl font-semibold">Review {c.handover ? 'handover' : 'show import'}</h2>
      )}
      {c.result ? (
        <StatusNotice tone="success" role="status">
          <p>
            {c.result.alreadyApplied
              ? 'Previously completed operation recovered.'
              : 'Import completed.'}
          </p>
          <Button
            onClick={() => {
              downloadJson(
                c.result!.receipt,
                c.handover ? 'handover-receipt.json' : 'import-receipt.json'
              )
              onReceiptSaved?.()
            }}
          >
            Download receipt
          </Button>
        </StatusNotice>
      ) : (
        <>
          {(!c.canApply || !!decisions.length) && (
            <StatusNotice tone={c.uncertain || decisions.length ? 'warning' : 'info'} role="status">
              {c.applying
                ? 'Importing reviewed records… Keep this page open.'
                : c.updating
                  ? 'Updating review… Previous results are not ready to apply.'
                  : c.uncertain
                    ? 'Import outcome unknown.'
                    : data && c.valid
                      ? decisions.length
                        ? `${decisions.length} ${decisions.length === 1 ? 'decision requires' : 'decisions require'} attention.`
                        : 'Review complete.'
                      : 'Choices are staged. Finish your decisions, then check the import before saving.'}
            </StatusNotice>
          )}
          {data && (
            <div className={'space-y-6'}>
              {!!decisions.length && (
                <nav aria-label="Import decisions" className="space-y-2">
                  <h3 className="font-semibold">Decisions required</h3>
                  <label className="block space-y-2">
                    <span className="text-sm text-muted-foreground">
                      Decision to review ({decisions.length})
                    </span>
                    <select
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                      value={active?.id ?? ''}
                      disabled={disabled}
                      onChange={event => {
                        setSelected(event.target.value)
                        setException('')
                      }}
                    >
                      {decisions.map(decision => (
                        <option key={decision.id} value={decision.id}>
                          {decision.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <details>
                    <summary>Ready groups and included records</summary>
                    {summary}
                  </details>
                </nav>
              )}
              <div
                ref={panel}
                tabIndex={-1}
                aria-label="Selected import decision"
                className="min-w-0 space-y-4"
              >
                {active ? (
                  <fieldset disabled={disabled} className="space-y-4">
                    <legend className="text-lg font-semibold">{active.title}</legend>
                    {active.type ? (
                      <>
                        <p>
                          {proposals.filter(ref => ref.type === active.type).length} unbound
                          records. Creating them adds distinct records even when names match records
                          already on this server.
                        </p>
                        <Button
                          onClick={() =>
                            save({
                              ...c.choices,
                              mappings: [
                                ...c.choices.mappings,
                                ...proposals
                                  .filter(
                                    ref =>
                                      ref.type === active.type &&
                                      !mappingKeys.has(referenceKey(ref))
                                  )
                                  .map(reference => ({ reference, action: 'create' as const })),
                              ],
                            })
                          }
                        >
                          Create these as new records
                        </Button>
                        <label className="block">
                          Map an exception
                          <select
                            className="block border rounded p-2 max-w-full"
                            value={exception}
                            onChange={e => setException(e.target.value)}
                          >
                            <option value="">Choose a record</option>
                            {proposals
                              .filter(ref => ref.type === active.type)
                              .map(ref => (
                                <option key={referenceKey(ref)} value={referenceKey(ref)}>
                                  {label(ref)} — {ref.key}
                                </option>
                              ))}
                          </select>
                        </label>
                        {exception &&
                          (() => {
                            const reference = proposals.find(ref => referenceKey(ref) === exception)
                            return reference ? mappingEditor(reference) : null
                          })()}
                      </>
                    ) : (
                      <>
                        {pendingBlockers
                          .filter(b =>
                            b.reference
                              ? active.reference &&
                                referenceKey(b.reference) === referenceKey(active.reference)
                              : b.code === active.id
                          )
                          .map((b, i) => (
                            <div key={i} className="space-y-2">
                              <StatusNotice
                                tone={
                                  [
                                    'FIELD_CONFLICT',
                                    'MAPPING_REQUIRED',
                                    'REMOVAL_REVIEW_REQUIRED',
                                  ].includes(b.code)
                                    ? 'warning'
                                    : 'error'
                                }
                                role="alert"
                              >
                                {(b.code === 'UNIQUE_DESTINATION_CONFLICT'
                                  ? 'A record on this server already uses a required unique value. Map to the existing record or change the file before continuing.'
                                  : b.message) ||
                                  'The server requires attention before this import can continue.'}
                              </StatusNotice>
                              <details>
                                <summary>Technical details</summary>
                                <p>{b.code}</p>
                              </details>
                              {b.reference &&
                                ['REMOVAL_REVIEW_REQUIRED', 'CONSUMED_IDENTITY_REMOVAL'].includes(
                                  b.code
                                ) &&
                                resolutions(
                                  b.reference,
                                  '$remove',
                                  ['identifiers', 'tombstones'].includes(b.reference.type)
                                    ? ['keepDestination']
                                    : ['keepDestination', 'usePackage']
                                )}
                            </div>
                          ))}
                        {clubAffiliationCodes.includes(active.id) &&
                          clubAffiliationEditor(
                            clubAffiliations.filter(item => item.action === 'review')
                          )}
                        {active.reference &&
                          data.blockers.some(
                            b =>
                              b.reference &&
                              referenceKey(b.reference) === referenceKey(active.reference) &&
                              /MAPPING|IMMUTABLE|CANONICAL_ORIGIN|DESTINATION|IDENTITY|BUSINESS_KEY/.test(
                                b.code
                              )
                          ) &&
                          mappingEditor(active.reference)}
                        {pendingChanges
                          .filter(
                            change =>
                              active.reference &&
                              referenceKey(change.reference) === referenceKey(active.reference)
                          )
                          .map(comparison)}
                      </>
                    )}
                  </fieldset>
                ) : (
                  summary
                )}
              </div>
            </div>
          )}
          {!!savedMappings.length && (
            <details onToggle={event => setEditSaved(event.currentTarget.open)}>
              <summary>Saved mapping decisions ({savedMappings.length})</summary>
              {editSaved && (
                <fieldset disabled={disabled} className="space-y-3">
                  <label>
                    Review a saved mapping
                    <select
                      className="block border rounded p-2 max-w-full"
                      value={savedChoice}
                      onChange={event => setSavedChoice(event.target.value)}
                    >
                      <option value="">Choose a record</option>
                      {savedMappings.map(mapping => (
                        <option
                          key={referenceKey(mapping.reference)}
                          value={referenceKey(mapping.reference)}
                        >
                          {label(mapping.reference)} — {mapping.reference.key} —{' '}
                          {mapping.description}
                        </option>
                      ))}
                    </select>
                  </label>
                  {(() => {
                    const mapping = savedMappings.find(
                      m => referenceKey(m.reference) === savedChoice
                    )
                    return mapping ? mappingEditor(mapping.reference) : null
                  })()}
                </fieldset>
              )}
            </details>
          )}
          {!!c.choices.resolutions.length && (
            <details>
              <summary>
                Saved conflict and removal decisions ({c.choices.resolutions.length})
              </summary>
              <fieldset disabled={disabled} className="space-y-3">
                <label>
                  Review a saved resolution
                  <select
                    className="block border rounded p-2 max-w-full"
                    value={savedResolution}
                    onChange={event => setSavedResolution(event.target.value)}
                  >
                    <option value="">Choose a decision</option>
                    {c.choices.resolutions.map(choice => (
                      <option key={referenceKey(choice)} value={referenceKey(choice)}>
                        {label(choice.reference)} — {choice.reference.key} —{' '}
                        {choice.field === '$remove' ? 'Removal' : choice.field} —{' '}
                        {choice.action === 'keepDestination'
                          ? 'Keep destination'
                          : choice.field === '$remove'
                            ? 'Approved removal'
                            : 'Use package value'}
                      </option>
                    ))}
                  </select>
                </label>
                {c.choices.resolutions.some(choice => referenceKey(choice) === savedResolution) && (
                  <>
                    <p>
                      Recheck this decision to choose again from the options currently permitted by
                      the server. Other saved decisions are retained.
                    </p>
                    <Button
                      onClick={() => {
                        c.changeChoices(
                          {
                            ...c.choices,
                            resolutions: c.choices.resolutions.filter(
                              choice => referenceKey(choice) !== savedResolution
                            ),
                          },
                          true
                        )
                        setSavedResolution('')
                      }}
                    >
                      Review this decision again
                    </Button>
                  </>
                )}
              </fieldset>
            </details>
          )}
          {(pkg?.previous || data?.blockers.some(b => /BASELINE/.test(b.code))) && (
            <section className="space-y-2" aria-label="Correction baselines">
              <JsonFile
                label="Add correction baseline"
                onReading={c.setReading}
                disabled={disabled}
                onLoad={value => {
                  ShowPackage.parse(value)
                  c.changeBaselines([...c.baselines, value])
                }}
              />
              <p>{c.baselines.length} correction baselines loaded.</p>
              {!!c.baselines.length && (
                <Button variant="outline" disabled={disabled} onClick={() => c.changeBaselines([])}>
                  Clear baselines
                </Button>
              )}
            </section>
          )}
          <WorkflowFooter
            secondary={
              <>
                {onBack && (
                  <Button variant="outline" disabled={disabled} onClick={onBack}>
                    Back
                  </Button>
                )}
                {!c.uncertain && (
                  <Button variant="outline" disabled={disabled} onClick={() => void c.preview()}>
                    {wizard ? 'Check import again' : 'Preview changes'}
                  </Button>
                )}
              </>
            }
          >
            {c.uncertain ? (
              <Button disabled={c.applying || c.disabled} onClick={() => void c.apply()}>
                Check import result
              </Button>
            ) : (
              data && (
                <Button
                  disabled={!c.canApply || disabled || !!decisions.length}
                  onClick={() => void c.apply()}
                >
                  {wizard
                    ? 'Import show'
                    : c.handover
                      ? 'Activate reviewed transfer'
                      : 'Apply reviewed changes'}
                </Button>
              )
            )}
          </WorkflowFooter>
          {data && !c.canApply && !c.uncertain && (
            <p className="text-sm text-muted-foreground">
              Import is unavailable until the current choices pass a fresh review and all decisions
              are resolved.
            </p>
          )}
          {c.uncertain && (
            <StatusNotice tone="warning" title="Don’t start another import yet">
              Check the result of this exact request first. Save a recovery file before leaving.
            </StatusNotice>
          )}
          {!!c.frozen && (
            <>
              <Check checked={recoveryAcknowledged} onChange={setRecoveryAcknowledged}>
                I acknowledge this recovery file contains the reviewed data in plaintext.
              </Check>
              <Button
                disabled={!recoveryAcknowledged}
                variant="outline"
                onClick={() =>
                  downloadJson(
                    {
                      format: 'honeyshow-review-recovery.v1',
                      installationKey: c.installationKey,
                      handover: c.handover,
                      request: c.frozen,
                    },
                    'show-review-recovery.json',
                    Boolean(c.delivery)
                  )
                }
              >
                Download sensitive recovery file
              </Button>
            </>
          )}
          <details>
            <summary className="text-sm">Resume a saved review</summary>
            <div className="mt-3">
              <JsonFile
                label="Load review recovery file"
                onReading={c.setReading}
                maxMiB={c.delivery ? 65 : 32}
                disabled={disabled}
                onLoad={c.recover}
              />
            </div>
          </details>
        </>
      )}
      {c.error && (
        <StatusNotice tone={c.uncertain ? 'warning' : 'error'} role="alert">
          {c.error}
        </StatusNotice>
      )}
    </section>
  )
}
